import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Step from '@mui/material/Step'
import StepButton from '@mui/material/StepButton'
import Stepper from '@mui/material/Stepper'
import Typography from '@mui/material/Typography'
import * as React from 'react'
import { useAppDispatch } from '../../app/hooks'
import { LabelStyled } from '../bespoke/request-quote/styled'
import { cleanUpGuslForm } from '../common/maintain-form/guslFormSlice'
import { ActionButtonStyled } from '../common/maintain-table/bootstrap/styled'

interface StepDefinition {
    label: string
    component: React.ReactNode
    isValid?: () => boolean
}

interface StepperProps {
    stepDefinitions: StepDefinition[]
    onComplete?: (data: any) => void
}

export function HorizontalNonLienarStepper({ stepDefinitions, onComplete }: StepperProps) {
    const [activeStep, setActiveStep] = React.useState(0)
    const [completed, setCompleted] = React.useState<{ [k: number]: boolean }>({})
    const [stepData, setStepData] = React.useState<{ [k: number]: any }>({})

    const dispatch = useAppDispatch()

    const totalSteps = () => stepDefinitions.length
    const completedSteps = () => Object.keys(completed).length
    const isLastStep = () => activeStep === totalSteps() - 1
    const allStepsCompleted = () => completedSteps() === totalSteps()

    const handleNext = () => {
        const newActiveStep =
            isLastStep() && !allStepsCompleted() ? stepDefinitions.findIndex((step, i) => !(i in completed)) : activeStep + 1
        setActiveStep(newActiveStep)
    }

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }

    const handleStep = (step: number) => () => {
        setActiveStep(step)
    }

    const handleStepDataChange = (stepIndex: number, data: any) => {
        setStepData((prev) => ({ ...prev, [stepIndex]: data }))
    }

    const getCurrentStepContent = () => {
        const currentStep = stepDefinitions[activeStep]
        if (React.isValidElement(currentStep.component)) {
            return React.cloneElement(currentStep.component as React.ReactElement, {
                onDataChange: (data: any) => handleStepDataChange(activeStep, data),
                stepData: stepData[activeStep],
                isValid: currentStep.isValid,
            })
        }
        return currentStep.component
    }

    return (
        <Box sx={{ width: '100%' }} p={2}>
            <Stepper nonLinear activeStep={activeStep}>
                {stepDefinitions.map((stepDef, index) => (
                    <Step key={stepDef.label} completed={completed[index]}>
                        <StepButton color="inherit" onClick={handleStep(index)}>
                            <LabelStyled>{stepDef.label}</LabelStyled>
                        </StepButton>
                    </Step>
                ))}
            </Stepper>

            <Box sx={{ mt: 3, mb: 2 }}>
                {allStepsCompleted() ? (
                    <React.Fragment>
                        <Typography sx={{ mt: 2, mb: 1 }}>All steps completed - you&apos;re finished</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                            <Box sx={{ flex: '1 1 auto' }} />
                            <Button
                                onClick={() => {
                                    setActiveStep(0)
                                    setCompleted({})
                                    setStepData({})
                                }}
                            >
                                Reset
                            </Button>
                        </Box>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        {/* Render current step content */}
                        <Box sx={{ minHeight: 400, p: 2 }}>{getCurrentStepContent()}</Box>

                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                            <ActionButtonStyled
                                withMargin
                                isAction
                                colorName={'default'}
                                disabled={activeStep === 0}
                                onClick={handleBack}
                                sx={{ mr: 1 }}
                            >
                                Back
                            </ActionButtonStyled>
                            <Box sx={{ flex: '1 1 auto' }} />
                            <ActionButtonStyled withMargin isAction colorName={'default'} onClick={handleNext} sx={{ mr: 1 }}>
                                Next
                            </ActionButtonStyled>
                            {activeStep !== stepDefinitions.length && (
                                <ActionButtonStyled withMargin isAction colorName={'save'} onClick={onComplete}>
                                    Save
                                </ActionButtonStyled>
                            )}
                        </Box>
                    </React.Fragment>
                )}
            </Box>
        </Box>
    )
}
